import React, { FunctionComponent, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View, ViewStyle } from 'react-native'

import { css } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { lightThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import { truncateEmail } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { isValidEmailString } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/validationUtils'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { CopyToClipboard } from '../../atoms/copyToClipboard/CopyToClipboard'
import { CheckIcon } from '../../atoms/icons/CheckIcon'
import { DownloadFileIcon } from '../../atoms/icons/DownloadFileIcon'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { Details } from '../../ui-models/messages/Details'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { ClientDetailsEpisodeSideBarSection } from '../clientDetailsSideBarSection/ClientDetailsEpisodeSideBarSection'
import { ClientDetailsMainSideBarSection } from '../clientDetailsSideBarSection/ClientDetailsMainSideBarSection'
import {
  ClientDetailsSideBarSection,
  ClientDetailsSideBarSectionItem,
} from '../clientDetailsSideBarSection/ClientDetailsSideBarSection'
import { DotItem, DotSeparatedRow } from '../dotSeparatedRow/DotSeparatedRow'
import { ProfileCardBadge, SPECIAL_ALERT_BADGE_TEXT } from '../profileCardBadge/ProfileCardBadge'

export interface ProfileCardProps {
  isLoading: boolean
  clientDetails: Details | null
  showExportButton?: boolean
  onExportClientRecords?: () => void
  showEmail?: boolean
  withoutHorizontalOrientation?: boolean
  onOpenProfile?: () => void
  hasClientDetailsPageInformation?: boolean
  showGuardianInfo?: boolean
  withBorder?: boolean
  riskBadgeSlot?: React.ReactNode
  style?: ViewStyle
}

type ProfileCardContentProps = Omit<ProfileCardProps, 'isLoading' | 'clientDetails'> & {
  clientDetails: Details
}

// ---------- Styles ----------

const Container = styled.View<{
  theme: ThemeType
  withoutHorizontalOrientation: boolean
  withBorder: boolean
}>(
  ({
    theme: {
      spacing,
      breakpoints: { isMinWidthLaptop },
    },
    withoutHorizontalOrientation,
    withBorder,
  }) => ({
    ...(withBorder && {
      borderWidth: 1,
      borderColor: lightThemeColors.borderDefault,
    }),
    backgroundColor: lightThemeColors.backgroundPrimary,
    borderRadius: spacing['16px'],
    padding: spacing['24px'],
    width: (isMinWidthLaptop || withoutHorizontalOrientation) && withBorder ? '288px' : '100%',
  }),
)

const ContentWrapper = styled.View<{ theme: ThemeType; withoutHorizontalOrientation: boolean }>(
  ({
    theme: {
      spacing,
      breakpoints: { isMinWidthLaptop },
    },
    withoutHorizontalOrientation,
  }) => ({
    flexDirection: isMinWidthLaptop || withoutHorizontalOrientation ? 'column' : 'row',
    width: '100%',
    gap: isMinWidthLaptop || withoutHorizontalOrientation ? 0 : spacing['24px'],
  }),
)

const TopSection = styled.View<{ theme: ThemeType; withoutHorizontalOrientation: boolean }>(
  ({
    theme: {
      spacing,
      breakpoints: { isMinWidthLaptop },
    },
    withoutHorizontalOrientation,
  }) => ({
    flex: isMinWidthLaptop || withoutHorizontalOrientation ? undefined : 0.6,
    ...((isMinWidthLaptop || withoutHorizontalOrientation) && {
      borderBottomWidth: 1,
      borderBottomColor: lightThemeColors.borderDefault,
      paddingBottom: spacing['32px'],
    }),
  }),
)

const BottomSection = styled.View<{ theme: ThemeType; withoutHorizontalOrientation: boolean }>(
  ({
    theme: {
      spacing,
      breakpoints: { isMinWidthLaptop },
    },
    withoutHorizontalOrientation,
  }) => ({
    paddingTop: isMinWidthLaptop || withoutHorizontalOrientation ? spacing['32px'] : '0px',
    flex: isMinWidthLaptop || withoutHorizontalOrientation ? undefined : 0.4,
    gap: spacing['32px'],
  }),
)

const CapsuleContainer = styled.View<{ theme: ThemeType; withoutHorizontalOrientation: boolean }>(
  ({
    theme: {
      spacing,
      breakpoints: { isMinWidthLaptop },
    },
    withoutHorizontalOrientation,
  }) => ({
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing['8px'],
    paddingBottom: isMinWidthLaptop || withoutHorizontalOrientation ? spacing['8px'] : '10px',
  }),
)

const TopDescriptions = styled.View<{ theme: ThemeType; withoutHorizontalOrientation: boolean }>(
  ({
    theme: {
      spacing,
      breakpoints: { isMinWidthLaptop },
    },
    withoutHorizontalOrientation,
  }) => ({
    flexDirection: isMinWidthLaptop || withoutHorizontalOrientation ? 'column' : 'row',
    gap: isMinWidthLaptop || withoutHorizontalOrientation ? spacing['4px'] : spacing['16px'],
  }),
)

const PhoneNumberContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  gap: spacing['4px'],
}))

const EmailContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  maxWidth: '100%',
  flexShrink: 1,
})

const EmailTextContainer = styled.View({
  maxWidth: '100%',
  flexShrink: 1,
})

const CopyIdButtonContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
}))

const ExportButtonContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  paddingTop: spacing['16px'],
  alignItems: 'flex-start',
}))

const OpenProfileButtonContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  paddingTop: spacing['16px'],
}))

// ---------- Main Component ----------

const ProfileCardContent: FunctionComponent<ProfileCardContentProps> = ({
  clientDetails,
  showExportButton = false,
  onExportClientRecords,
  showEmail = false,
  withoutHorizontalOrientation = false,
  onOpenProfile,
  hasClientDetailsPageInformation = true,
  showGuardianInfo = hasClientDetailsPageInformation,
  withBorder = true,
  riskBadgeSlot,
  style,
}) => {
  const {
    colors,
    breakpoints: { isMinWidthLaptop },
  } = useTheme() as ThemeType
  const intl = useIntl()
  const { formatMessage } = intl

  const {
    firstName: clientFirstName,
    preferredName: clientPreferredName,
    legalName: clientLegalName,
    country: clientCountry,
    age,
    phoneNumber,
    canLeaveVoicemail,
    dob,
    gender,
    ethnicity,
    displayTimeZone,
    lyraId,
    pronouns: formattedPronouns,
    email,

    showSpecialAlertBadge,
    specialAlertType,
    hideClientContactInfo,
    hideClientPhone,
    mosId,
    lyraCode,
  } = clientDetails.client

  const { programName } = clientDetails.episode

  // Build top items once, keep code DRY
  const topItems = useMemo<DotItem[]>(() => {
    const nameItem = clientLegalName ? (
      <BodyText
        text={
          <FormattedMessage
            defaultMessage='{clientName} (legal)'
            description="Label for client's legal name"
            values={{ clientName: clientLegalName }}
          />
        }
        size={BodyTextSize.DEFAULT}
        testID={tID('ProfileCard-legalName')}
      />
    ) : undefined

    const pronouns = formattedPronouns ? (
      <BodyText
        text={
          <FormattedMessage
            defaultMessage='{formattedPronouns} '
            description="Label for client's pronouns"
            values={{ formattedPronouns }}
          />
        }
        size={BodyTextSize.DEFAULT}
        testID={tID('ProfileCard-pronouns')}
      />
    ) : undefined

    const ageItem = age ? (
      <BodyText
        text={<FormattedMessage defaultMessage='Age {age}' description="Label for client's age" values={{ age }} />}
        size={BodyTextSize.DEFAULT}
        testID={tID('ProfileCard-age')}
      />
    ) : undefined

    const voicemailToolTipContent = (
      <FormattedMessage
        defaultMessage='{clientFirstName} provided voicemail consent'
        description='Tooltip message indicating that the client has provided voicemail consent'
        values={{ clientFirstName }}
      />
    )

    const phoneItem = phoneNumber ? (
      <PhoneNumberContainer>
        <BodyText
          text={
            <FormattedMessage
              defaultMessage='{phone} '
              description='Label phone number'
              values={{ phone: phoneNumber }}
            />
          }
          size={BodyTextSize.DEFAULT}
          testID={tID('ProfileCard-phoneNumber')}
        />
        {canLeaveVoicemail && (
          <View testID={tID('Voicemail-checkmark')}>
            <Tooltip placement='top' content={voicemailToolTipContent}>
              <CheckIcon fillColor={colors.iconSuccess} size={24} />
            </Tooltip>
          </View>
        )}
      </PhoneNumberContainer>
    ) : undefined

    const emailItem =
      showEmail && email && isValidEmailString(email) ? (
        <EmailContainer>
          <Tooltip content={email} placement='bottom' hoverEnabled>
            <EmailTextContainer>
              <BodyText
                text={truncateEmail(email)}
                size={BodyTextSize.DEFAULT}
                testID={tID('ProfileCard-email')}
                numberOfLines={1}
              />
            </EmailTextContainer>
          </Tooltip>
          <CopyToClipboard
            copyText={email}
            fillColor={colors.iconActive}
            size={18}
            testID={tID('ProfileCard-email-copy')}
            accessibilityLabel={formatMessage({
              defaultMessage: 'Copy email address',
              description: 'Accessibility label for the button that copies client email to the clipboard',
            })}
          />
        </EmailContainer>
      ) : undefined

    return [
      nameItem,
      pronouns,
      ageItem,
      hideClientContactInfo || hideClientPhone ? undefined : phoneItem,
      hideClientContactInfo ? undefined : emailItem,
    ].filter(Boolean)
  }, [
    clientLegalName,
    formattedPronouns,
    age,
    phoneNumber,
    canLeaveVoicemail,
    colors.iconSuccess,
    colors.iconActive,
    clientFirstName,
    showEmail,
    email,
    formatMessage,
    hideClientContactInfo,
    hideClientPhone,
  ])

  // Build bottom items for ClientDetailsSideBarSection
  const bottomItems: ClientDetailsSideBarSectionItem[] = useMemo(
    () =>
      [
        dob
          ? {
              key: 'dob',
              label: <FormattedMessage defaultMessage='DOB' description='Label for Date of Birth' />,
              textValue: dob,
              testID: tID('ProfileCard-dob'),
            }
          : null,
        gender
          ? {
              key: 'gender',
              label: <FormattedMessage defaultMessage='Gender' description="Label for client's gender" />,
              textValue: gender,
              testID: tID('ProfileCard-gender'),
            }
          : null,
        ethnicity
          ? {
              key: 'ethnicity',
              label: <FormattedMessage defaultMessage='Ethnicity' description="Label for client's ethnicity" />,
              textValue: ethnicity,
              testID: tID('ProfileCard-ethnicity'),
            }
          : null,
        displayTimeZone
          ? {
              key: 'timezone',
              label: <FormattedMessage defaultMessage='Timezone' description="Label for client's timezone" />,
              textValue: displayTimeZone,
              testID: tID('ProfileCard-timezone'),
            }
          : null,
        clientCountry
          ? {
              key: 'country',
              label: <FormattedMessage defaultMessage='Country' description="Label for client's country" />,
              textValue: clientCountry,
              testID: tID('ProfileCard-country'),
            }
          : null,
        lyraId
          ? {
              key: 'lyraId',
              label: <FormattedMessage defaultMessage='Lyra ID' description='Label for the Lyra ID field' />,
              value: (
                <CopyIdButtonContainer>
                  <CopyToClipboard
                    displayTextSize={
                      isMinWidthLaptop || withoutHorizontalOrientation ? BodyTextSize.DEFAULT : BodyTextSize.SMALL
                    }
                    displayText={
                      <FormattedMessage
                        defaultMessage='Copy ID'
                        description='Label for the button that copies client`s Lyra ID to the clipboard'
                      />
                    }
                    accessibilityLabel={formatMessage({
                      defaultMessage: 'Copy ID',
                      description: 'Accessibility label for the button that copies client`s Lyra ID to the clipboard',
                    })}
                    copyText={lyraId}
                    containerCustomStyle={css`
                      flex-direction: row-reverse;
                      justify-content: center;
                      align-items: center;
                      gap: 8px;
                      text-decoration-line: underline;
                      text-decoration-color: ${colors.textActive};
                    `}
                  />
                </CopyIdButtonContainer>
              ),
              testID: tID('ProfileCard-lyraId'),
            }
          : null,
        mosId
          ? {
              key: 'mosId',
              label: <FormattedMessage defaultMessage='Case Number' description='Label for the Leidos case number' />,
              value: (
                <CopyIdButtonContainer>
                  <CopyToClipboard
                    displayTextSize={
                      isMinWidthLaptop || withoutHorizontalOrientation ? BodyTextSize.DEFAULT : BodyTextSize.SMALL
                    }
                    displayText={
                      <FormattedMessage
                        defaultMessage='Copy Case Number'
                        description='Label for the button that copies the client Leidos case number to the clipboard'
                      />
                    }
                    accessibilityLabel={formatMessage({
                      defaultMessage: 'Copy Case Number',
                      description: 'Accessibility label for the button that copies the client Leidos case number',
                    })}
                    copyText={mosId}
                    containerCustomStyle={css`
                      flex-direction: row-reverse;
                      justify-content: center;
                      align-items: center;
                      gap: 8px;
                      text-decoration-line: underline;
                      text-decoration-color: ${colors.textActive};
                    `}
                  />
                </CopyIdButtonContainer>
              ),
              testID: tID('ProfileCard-mosId'),
            }
          : null,
        lyraCode
          ? {
              key: 'lyraCode',
              label: <FormattedMessage defaultMessage='Lyra Code' description='Label for the Lyra Code field' />,
              value: (
                <CopyIdButtonContainer>
                  <CopyToClipboard
                    displayTextSize={
                      isMinWidthLaptop || withoutHorizontalOrientation ? BodyTextSize.DEFAULT : BodyTextSize.SMALL
                    }
                    displayText={
                      <FormattedMessage
                        defaultMessage='Copy Lyra Code'
                        description='Label for the button that copies client Lyra Code to the clipboard'
                      />
                    }
                    accessibilityLabel={formatMessage({
                      defaultMessage: 'Copy Lyra Code',
                      description: 'Accessibility label for the button that copies client Lyra Code to the clipboard',
                    })}
                    copyText={lyraCode}
                    containerCustomStyle={css`
                      flex-direction: row-reverse;
                      justify-content: center;
                      align-items: center;
                      gap: 8px;
                      text-decoration-line: underline;
                      text-decoration-color: ${colors.textActive};
                    `}
                  />
                </CopyIdButtonContainer>
              ),
              testID: tID('ProfileCard-lyraCode'),
            }
          : null,
      ].filter(Boolean) as ClientDetailsSideBarSectionItem[],
    [
      dob,
      gender,
      ethnicity,
      displayTimeZone,
      lyraId,
      isMinWidthLaptop,
      colors.textActive,
      formatMessage,
      clientCountry,
      withoutHorizontalOrientation,
      mosId,
      lyraCode,
    ],
  )

  // Build guardian items for ClientDetailsSideBarSection
  const guardian = clientDetails.guardian
  const hasGuardianInfo = Boolean(guardian?.firstName || guardian?.lastName || guardian?.phone || guardian?.email)
  const guardianItems: ClientDetailsSideBarSectionItem[] = useMemo(
    () =>
      [
        guardian?.firstName || guardian?.lastName
          ? {
              key: 'guardianName',
              label: <FormattedMessage defaultMessage='Name' description="Label for guardian's name" />,
              textValue: [guardian?.firstName, guardian?.lastName].filter(Boolean).join(' '),
              testID: tID('ProfileCard-guardianName'),
            }
          : null,
        guardian?.phone
          ? {
              key: 'guardianPhone',
              label: <FormattedMessage defaultMessage='Phone' description="Label for guardian's phone number" />,
              textValue: guardian.phone,
              testID: tID('ProfileCard-guardianPhone'),
            }
          : null,
        guardian?.email
          ? {
              key: 'guardianEmail',
              label: <FormattedMessage defaultMessage='Email' description="Label for guardian's email" />,
              value: (
                <EmailContainer>
                  <Tooltip content={guardian.email} placement='bottom' hoverEnabled>
                    <EmailTextContainer>
                      <BodyText
                        text={truncateEmail(guardian.email, 15, true)}
                        size={
                          isMinWidthLaptop || withoutHorizontalOrientation ? BodyTextSize.DEFAULT : BodyTextSize.SMALL
                        }
                        testID={tID('ProfileCard-guardianEmail')}
                        numberOfLines={1}
                      />
                    </EmailTextContainer>
                  </Tooltip>
                  <CopyToClipboard
                    copyText={guardian.email}
                    fillColor={colors.iconActive}
                    size={18}
                    testID={tID('ProfileCard-guardianEmail-copy')}
                    accessibilityLabel={formatMessage({
                      defaultMessage: 'Copy guardian email address',
                      description: 'Accessibility label for the button that copies guardian email to the clipboard',
                    })}
                  />
                </EmailContainer>
              ),
              testID: tID('ProfileCard-guardianEmail'),
            }
          : null,
      ].filter(Boolean) as ClientDetailsSideBarSectionItem[],
    [
      guardian?.firstName,
      guardian?.lastName,
      guardian?.phone,
      guardian?.email,
      isMinWidthLaptop,
      withoutHorizontalOrientation,
      colors.iconActive,
      formatMessage,
    ],
  )

  return (
    <Container
      testID={tID('ProfileCard')}
      withoutHorizontalOrientation={withoutHorizontalOrientation}
      withBorder={withBorder}
      style={style}
    >
      <ContentWrapper withoutHorizontalOrientation={withoutHorizontalOrientation}>
        <TopSection withoutHorizontalOrientation={withoutHorizontalOrientation}>
          <CapsuleContainer withoutHorizontalOrientation={withoutHorizontalOrientation}>
            {/* Special Alert Badge */}
            {showSpecialAlertBadge && (
              <ProfileCardBadge text={SPECIAL_ALERT_BADGE_TEXT} specialAlertType={specialAlertType} />
            )}

            {/* Risk Badge */}
            {riskBadgeSlot}

            {/* Program Badge */}
            {programName && <ProfileCardBadge text={getProgramDisplayLabel(intl, programName, 'clientProfile')} />}
          </CapsuleContainer>
          <Title text={clientPreferredName || clientLegalName} size={TitleSize.LARGE} />
          {hasClientDetailsPageInformation && (
            <TopDescriptions withoutHorizontalOrientation={withoutHorizontalOrientation}>
              {isMinWidthLaptop || withoutHorizontalOrientation ? (
                topItems.map((item, idx) =>
                  typeof item === 'string' ? (
                    <BodyText key={idx} text={item} size={BodyTextSize.DEFAULT} />
                  ) : (
                    <React.Fragment key={idx}>{item}</React.Fragment>
                  ),
                )
              ) : (
                <DotSeparatedRow items={topItems} />
              )}
            </TopDescriptions>
          )}
          {!hasClientDetailsPageInformation && (
            <OpenProfileButtonContainer>
              <SecondaryButton
                fullWidth
                testID={tID('ClientDetailsSideBar-open-profile')}
                text={
                  <FormattedMessage defaultMessage='Open full profile' description='Button to open client profile' />
                }
                onPress={() => onOpenProfile?.()}
              />
            </OpenProfileButtonContainer>
          )}
        </TopSection>

        <BottomSection withoutHorizontalOrientation={withoutHorizontalOrientation}>
          {hasClientDetailsPageInformation ? (
            <ClientDetailsSideBarSection
              headerText={
                isMinWidthLaptop || withoutHorizontalOrientation ? (
                  <FormattedMessage
                    defaultMessage='Additional Information'
                    description='Display label for the bottom descriptions for client'
                  />
                ) : undefined
              }
              items={bottomItems}
              withBreakpoints={!withoutHorizontalOrientation}
              valueTextSize={
                isMinWidthLaptop || withoutHorizontalOrientation ? BodyTextSize.DEFAULT : BodyTextSize.SMALL
              }
            />
          ) : (
            <>
              <ClientDetailsMainSideBarSection details={clientDetails} />
              <ClientDetailsEpisodeSideBarSection details={clientDetails} />
            </>
          )}
          {showGuardianInfo && hasGuardianInfo && (
            <ClientDetailsSideBarSection
              headerText={
                isMinWidthLaptop || withoutHorizontalOrientation ? (
                  <FormattedMessage
                    defaultMessage='Caregiver'
                    description='Display label for the caregiver information section'
                  />
                ) : undefined
              }
              items={guardianItems}
              withBreakpoints={!withoutHorizontalOrientation}
              valueTextSize={
                isMinWidthLaptop || withoutHorizontalOrientation ? BodyTextSize.DEFAULT : BodyTextSize.SMALL
              }
            />
          )}
          {showExportButton && onExportClientRecords && (
            <ExportButtonContainer>
              <SecondaryButton
                text={
                  <FormattedMessage
                    defaultMessage='Export Client Records'
                    description='Button text for exporting client records'
                  />
                }
                leftIcon={<DownloadFileIcon size={24} fill={colors.iconActive} />}
                onPress={onExportClientRecords}
                testID={tID('ProfileCard-export-button')}
                isSmall
              />
            </ExportButtonContainer>
          )}
        </BottomSection>
      </ContentWrapper>
    </Container>
  )
}

export const ProfileCard: FunctionComponent<ProfileCardProps> = ({ isLoading, clientDetails, ...props }) => {
  if (isLoading || !clientDetails) {
    return null
  }

  return <ProfileCardContent {...props} clientDetails={clientDetails} />
}
